import React from "react";
import { Navigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { Permission } from "@/shared/constants/permissions";
import { FullPageLoadingSpinner } from "@/components/ui/loading-spinner";

interface PermissionGuardProps {
  children: React.ReactNode;
  permissions: Permission[];
  requireAll?: boolean;
  fallbackPath?: string;
}

/**
 * Permission-based access guard
 * Checks if user has required permission(s) before granting access
 * Shows loading spinner while validating
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  children, 
  permissions, 
  requireAll = false,
  fallbackPath = "/"
}) => {
  const { isAuthenticated, isLoading, hasAnyPermission, hasAllPermissions } = useUser();

  if (isLoading) {
    return <FullPageLoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

